import React, { useState, useEffect, useRef } from 'react';
import { getAIResponse, availableModels } from '../utils/aiModels';
import { FiSend, FiX, FiSettings, FiMessageSquare } from 'react-icons/fi';

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [settings, setSettings] = useState({
    enabled: true,
    apiKey: '',
    welcomeMessage: 'Hello! How can I help you today?',
    aiEnabled: true,
    aiModel: 'claude-3-sonnet',
    temperature: 0.7,
    maxTokens: 500,
    responses: []
  });

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('chatbotSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(parsedSettings);
      } catch (error) {
        console.error('Error parsing chatbot settings:', error);
        setError('Error loading chatbot settings. Please refresh the page.');
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'assistant', content: settings.welcomeMessage }]);
    }
  }, [isOpen, settings.welcomeMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getModelName = (modelId) => {
    const model = availableModels.find(m => m.id === modelId);
    return model ? model.name : 'AI Assistant';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setError(null);

    try {
      // Convert manual responses to the format expected by getAIResponse
      const customResponses = settings.responses.reduce((acc, response) => {
        acc[response.question] = response.answer;
        return acc;
      }, {});

      const response = await getAIResponse(userMessage, {
        modelId: settings.aiModel,
        temperature: settings.temperature,
        maxTokens: settings.maxTokens,
        anthropicKey: settings.apiKey,
        customResponses
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response,
        model: settings.aiModel
      }]);
    } catch (err) {
      console.error('Error getting AI response:', err);
      const errorMessage = err.message || 'Sorry, I encountered an error. Please try again.';
      setError(errorMessage);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: errorMessage,
            isError: true
      }]);
        } finally {
      setIsLoading(false);
    }
  };

  if (!settings.enabled) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-primary text-white p-4 rounded-full shadow-lg hover:bg-primary/90 transition-colors"
          aria-label="Open chat"
        >
          <FiMessageSquare size={24} />
        </button>
      )}

        {isOpen && (
        <div className="bg-gray-900 rounded-lg shadow-xl w-96 h-[600px] flex flex-col">
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <div>
              <h3 className="text-white font-semibold">Chat with AI Assistant</h3>
              <p className="text-sm text-gray-400">Using: {getModelName(settings.aiModel)}</p>
              </div>
              <button 
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white"
                aria-label="Close chat"
              >
              <FiX size={20} />
              </button>
            </div>
            
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-primary text-white'
                      : message.isError
                        ? 'bg-red-900 text-white'
                        : 'bg-gray-800 text-white'
                  }`}
                >
                  {message.content}
                  {message.role === 'assistant' && message.model && !message.isError && (
                    <div className="text-xs text-gray-400 mt-1">
                      {getModelName(message.model)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-800 text-white rounded-lg p-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                        </div>
                      </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            
          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-700">
            <div className="flex space-x-2">
                <input
                  type="text"
                  value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isLoading}
              />
              <button
                  type="submit"
                disabled={isLoading || !input.trim()}
                className="bg-primary text-white p-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiSend size={20} />
              </button>
              </div>
            </form>
        </div>
        )}
    </div>
  );
};

export default AIChatbot; 